'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from '@core/utils';
import { usePathname } from 'next/navigation';
import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { DonationBnb } from '@/app/_components/FloatingBnbController/_components/DonationBnb';
import { LocalMarketBnb } from '@/app/_components/FloatingBnbController/_components/LocalMarketBnb';
import { getFloatingBnbKind } from '@/app/_components/FloatingBnbController/constants';
import { GlobalNavigation } from '@/app/_components/GlobalNavigation';
import { useGlobalStore } from '@/stores/GlobalStore';
import { hasWelloBottomNav } from '@/utils';

//* 셸 지오메트리 전이(duration-300)와 퇴장 레이어 언마운트 유예가 같은 시계를 쓴다.
const SHELL_TRANSITION_DURATION = 300;
type BottomNavState = 'wello' | 'donation' | 'localMarket' | 'none';
type VisibleNavState = Exclude<BottomNavState, 'none'>;
interface BottomNavLayerProps {
  isActive: boolean;
  isEntering: boolean;
  className?: string;
  children: ReactNode;
}
const BottomNavLayer = t0 => {
  const $ = _c(8);
  const {
    isActive,
    isEntering,
    className,
    children
  } = t0;
  const t1 = !isActive || undefined;
  const t2 = isEntering && "animate-in fade-in-0 motion-reduce:animate-none";
  const t3 = isActive ? "opacity-100" : "pointer-events-none opacity-0";
  let t4;
  if ($[0] !== className || $[1] !== t2 || $[2] !== t3) {
    t4 = cn("absolute transition-opacity duration-200 motion-reduce:transition-none", t2, t3, className);
    $[0] = className;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== children || $[5] !== t1 || $[6] !== t4) {
    t5 = <div inert={t1} className={t4}>{children}</div>;
    $[4] = children;
    $[5] = t1;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
};

/**
 * 하단 네비게이션 셸 — 웰로 사각 탭바·고기부/웰마켙 pill이 별개 DOM으로 갈라져 있으면
 * 섹션 전환 때 즉시 교체돼 형태 전환이 끊긴다. 영속 컨테이너 하나가 지오메트리(위치·높이·
 * radius·그림자)를 전이하고, 탭줄은 활성 레이어 + 퇴장 레이어(전이 시간만 유예 마운트)의
 * 크로스페이드로 갈아탄다. 상시 마운트가 아니라 2슬롯인 이유: 숨은 레이어도 마운트돼 있으면
 * adpopcorn 인증 쿼리·라우트 prefetch가 전 페이지에서 상시 실행되고 드롭다운 열림 상태가
 * 서비스 복귀 시 되살아난다. 숨김 상태는 마지막 가시 형태를 유지한 채 opacity로만 빠진다.
 */
export const BottomNavShell = () => {
  const $ = _c(32);
  const pathname = usePathname();
  const hideBottomNav = useGlobalStore(_temp);
  let t0;
  if ($[0] !== hideBottomNav || $[1] !== pathname) {
    t0 = hideBottomNav ? "none" : getFloatingBnbKind(pathname) ?? (hasWelloBottomNav(pathname, hideBottomNav) ? "wello" : "none");
    $[0] = hideBottomNav;
    $[1] = pathname;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const state_0 = t0;
  const [previousState, setPreviousState] = useState(state_0);
  const [exitingState, setExitingState] = useState(null);
  const [lastVisibleState, setLastVisibleState] = useState(state_0 === "none" ? "wello" : state_0);
  if (previousState !== state_0) {
    setPreviousState(state_0);
    setExitingState(previousState === "none" ? null : previousState);
    if (state_0 !== "none") {
      setLastVisibleState(state_0);
    }
  }
  let t1;
  let t2;
  if ($[3] !== exitingState) {
    t1 = () => {
      if (!exitingState) {
        return;
      }
      const unmountTimeoutId = window.setTimeout(() => {
        setExitingState(null);
      }, SHELL_TRANSITION_DURATION);
      return () => {
        window.clearTimeout(unmountTimeoutId);
      };
    };
    t2 = [exitingState];
    $[3] = exitingState;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  const shapeState = state_0 === "none" ? lastVisibleState : state_0;
  const isPillShape = shapeState !== "wello";
  const shellRef = useRef(null);
  let t3;
  let t4;
  if ($[6] !== state_0) {
    t3 = () => {
      const shell = shellRef.current;
      if (!shell) {
        return;
      }
      const root = document.documentElement;
      root.style.removeProperty("--bottom-nav-height");
      root.style.removeProperty("--floating-bnb-height");
      if (state_0 === "none") {
        return;
      }
      const activeVariable = state_0 === "wello" ? "--bottom-nav-height" : "--floating-bnb-height";
      const publishOccupiedInset = () => {
        if (shell.offsetHeight === 0) {
          root.style.removeProperty(activeVariable);
          return;
        }
        root.style.setProperty(activeVariable, `${Math.round(window.innerHeight - shell.getBoundingClientRect().top)}px`);
      };
      publishOccupiedInset();
      const resizeObserver = new ResizeObserver(publishOccupiedInset);
      resizeObserver.observe(shell);
      window.addEventListener("resize", publishOccupiedInset);
      return () => {
        resizeObserver.disconnect();
        window.removeEventListener("resize", publishOccupiedInset);
        root.style.removeProperty(activeVariable);
      };
    };
    t4 = [state_0];
    $[6] = state_0;
    $[7] = t3;
    $[8] = t4;
  } else {
    t3 = $[7];
    t4 = $[8];
  }
  useEffect(t3, t4);
  let t5;
  if ($[9] !== exitingState || $[10] !== state_0) {
    t5 = layer => state_0 === layer || exitingState === layer;
    $[9] = exitingState;
    $[10] = state_0;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const isLayerMounted = t5;
  const t6 = isPillShape ? "inset-x-20 bottom-[calc(10px+var(--safe-area-bottom,0px))] z-20 h-60 rounded-[30px] border-transparent shadow-[0px_0px_30px_0px_rgba(0,0,0,0.05),0px_16px_50px_0px_rgba(0,0,0,0.05)]" : "inset-x-0 bottom-0 z-10 h-[calc(70px+var(--safe-area-bottom,0px))] rounded-none border-Gray-50 shadow-none";
  const t7 = state_0 === "none" && "pointer-events-none opacity-0";
  let t8;
  if ($[12] !== t6 || $[13] !== t7) {
    t8 = cn("fixed hidden select-none border-t-1 bg-Gray-white mobile-tablet:block", "transition-[left,right,bottom,height,border-radius,border-color,box-shadow,opacity] duration-300 motion-reduce:transition-none", t6, t7);
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== exitingState || $[16] !== isLayerMounted || $[17] !== state_0) {
    t9 = isLayerMounted("wello") && <BottomNavLayer className="inset-x-0 top-0" isActive={state_0 === "wello"} isEntering={state_0 === "wello" && exitingState !== null}><GlobalNavigation /></BottomNavLayer>;
    $[15] = exitingState;
    $[16] = isLayerMounted;
    $[17] = state_0;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== exitingState || $[20] !== isLayerMounted || $[21] !== state_0) {
    t10 = isLayerMounted("donation") && <BottomNavLayer className="inset-0" isActive={state_0 === "donation"} isEntering={state_0 === "donation" && exitingState !== null}><ErrorBoundary fallback={null}><DonationBnb /></ErrorBoundary></BottomNavLayer>;
    $[19] = exitingState;
    $[20] = isLayerMounted;
    $[21] = state_0;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== exitingState || $[24] !== isLayerMounted || $[25] !== state_0) {
    t11 = isLayerMounted("localMarket") && <BottomNavLayer className="inset-0" isActive={state_0 === "localMarket"} isEntering={state_0 === "localMarket" && exitingState !== null}><ErrorBoundary fallback={null}><LocalMarketBnb /></ErrorBoundary></BottomNavLayer>;
    $[23] = exitingState;
    $[24] = isLayerMounted;
    $[25] = state_0;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t10 || $[28] !== t11 || $[29] !== t8 || $[30] !== t9) {
    t12 = <div ref={shellRef} className={t8}>{t9}{t10}{t11}</div>;
    $[27] = t10;
    $[28] = t11;
    $[29] = t8;
    $[30] = t9;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  return t12;
};
function _temp(state) {
  return state.hideBottomNav;
}